/**
 * UBER CRITICAL UPDATE - 8/7/2025
 * Fixed duplicate message bug in handleNewMessage (lines 396-398)
 * Changed: Used updatedMessages instead of currentMessages in final array
 * Previous: Stale closure was causing user messages to be added twice
 * Impact: Fixed +1 message count bug, eliminated duplicate messages
 * Revert: git checkout d0e0cb9 -- frontend/src/components/chatWindowMsgHandler.js
 * 
 * AMENDMENT - Streaming Jump Fix (8/7/2025)
 * Fixed visual jump after streaming completion (lines 183-234, 363-416)
 * Changed: Pre-generate Firebase ID before streaming instead of using temporary ID
 * Previous: ID change from tempMessageId to botMessageId caused React remount
 * Impact: Eliminated visual jump, achieved buttery smooth streaming
 * Technical: Same message ID throughout lifecycle prevents component remount
 */

/**
 * useMessageHandlers - Custom hook for managing chat messaging with streaming responses
 * 
 * This hook provides a complete message handling system for chat interfaces with AI responses.
 * It manages the entire lifecycle of messages from user input through streaming responses
 * to Firebase persistence, while protecting against common race conditions.
 * 
 * Core Functionality:
 * - Handles first-time and subsequent message exchanges
 * - Streams real-time bot responses with temporary IDs during streaming
 * - Persists messages to Firebase with proper conversation tracking
 * - Protects against UI state rollbacks (preventing message disappearance)
 * - Enforces user message limits and subscription tiers
 * - Supports different response modes (normal, quick, deep, search)
 * - Maintains session continuity across page navigation
 * 
 * 
 * @returns {Object} Object containing:
 * - handleFirstMessage: Function to handle the first message in a conversation
 * - handleNewMessage: Function to handle subsequent messages
 * - filterDuplicateMessages: Function to filter duplicate messages from Firebase
 * - isStreaming: Function that returns whether messages are currently streaming
 * - resetFirstAfterTransition: Function to reset transition state
 * 
 * Message Flow:
 * 1. User sends message
 * 2. Message is saved to Firebase (creates new conversation if needed)
 * 3. UI updates with user message immediately
 * 4. API call is made with the message (with appropriate mode modifier)
 * 5. Response streams in real-time with a temporary ID
 * 6. On completion, bot message is saved to Firebase with permanent ID
 * 7. UI updates with final message (real Firebase ID)
 * 8. Both message IDs are tracked to prevent duplication
 * 9. Usage counter is incremented and session storage is updated
 * 
 * Message Streaming:
 * - During streaming, messages are given temporary IDs for UI updates
 * - The streaming flag prevents Firebase updates from interfering
 * - Once streaming completes, the final message is saved with a permanent ID
 * - Both user and bot message IDs are tracked to prevent duplications
 * 
 * Race Condition Protection:
 * The hook implements a mechanism to prevent messages from disappearing after
 * being displayed correctly. This protection:
 * 
 * 1. Uses recentlyCompletedMessageRef to track when messages have just been added
 * 2. Tracks the current conversation with lastConversationIdRef
 * 3. Selectively blocks Firebase updates that would cause message loss
 * 4. Automatically clears the protection after 2 seconds
 * 5. Resets protection when switching conversations
 * 
 * This ensures users don't experience the frustration of seeing messages
 * appear and then disappear, particularly when returning to idle chats
 * or when network conditions cause delayed Firebase updates.
 * 
 * Session Persistence:
 * - Messages and conversation state are stored in sessionStorage
 * - This maintains continuity when navigating between pages
 * - When returning to a chat, the state is restored from storage
 * 
 * Response Modes:
 * The hook supports different response modes, adding appropriate modifiers:
 * - normal: Standard AI responses (default)
 * - quick: Shorter, more direct responses
 * - deep: Detailed, in-depth analysis
 * - search: Web search functionality for current topics
 */

import { useState, useCallback, useRef } from 'react';
import { saveMessage } from '../utils/db-messaging';
import { updatePointsOnConversation } from '../userservices/updatePointsOnConversation';
import { updatePointsOnMessage } from '../userservices/updatePointsOnMessage';
import { config } from '../config';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { trackFirstMessage } from '../utils/pixels';

export const useMessageHandlers = ({
  userId,
  messages,
  setMessages, 
  setIsFirstMessage,
  setIsLoading,
  setError,
  checkLimit,
  canSendMessage,
  setShowUpgradePrompt,
  incrementUsage,
  activeConversation,
  selectConversation,
  language,
  responseMode = 'normal'
}) => {
  // Track whether we're currently in a streaming operation
  const isStreamingRef = useRef(false);
  // Track message IDs we've already processed to avoid duplication
  const processedMessageIds = useRef(new Set());
  // Track if this is the first message after homepage transition
  const isFirstAfterTransitionRef = useRef(true);
  // Add this reference to track the current conversation ID
  const lastConversationIdRef = useRef(null);
// This is the key to preventing message disappearance after streaming completes
  const recentlyCompletedMessageRef = useRef(false);

  // Define response mode suffixes
  const RESPONSE_MODES = {
    normal: "",
    quick: "[respond briefly and directly]",
    deep: "[provide detailed statistical analysis]",
    search: "[. Use this to search these terms for news and provide relevant content and articles]"
  };

  const handleFirstMessage = async (message) => {
    setIsLoading(true);
    setError(null);
    isStreamingRef.current = true;
  
    try {
      await checkLimit();
      if (!canSendMessage) {
        setShowUpgradePrompt(true);
        setIsLoading(false);
        isStreamingRef.current = false;
        return;
      }
  
      // First save user message and create new conversation
      const { messageId, conversationId } = await saveMessage(userId, { 
        sender: "user", 
        text: message.text // Save original message
      });

      // Track first message event for Facebook Pixel
      trackFirstMessage(userId, message.text.length);

      // Make sure conversation is selected before proceeding
      if (conversationId) {
        await selectConversation({ id: conversationId });
      }

      // Update messages immediately with user message only - don't add bot message yet
      const userMessage = { id: messageId, sender: "user", text: message.text };
      const updatedMessages = [userMessage];
      setMessages(updatedMessages);

      // Choose suffix based on message type or current mode
      let suffix = "";
      if (message.isSearch) {
        suffix = RESPONSE_MODES.search;
      } else {
        suffix = RESPONSE_MODES[responseMode] || "";
      }

      // Add suffix only for API call
      const apiMessage = message.text + suffix;

      const response = await fetch(`${config.apiUrl}/ai/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: apiMessage, // Use modified message
          history: [],
          language,
          metadata: { userId: userId, conversationId: conversationId}  // As a nested metadata object
        }),
      });
  
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      // Handle the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let botMessage = "";
      
      // Generate the Firebase ID upfront to maintain consistency
      const { messageId: botMessageId } = await saveMessage(userId, { 
        sender: "bot", 
        text: "" // Save placeholder first
      }, conversationId);

      // Add both message IDs to processed set to avoid duplication
      processedMessageIds.current.add(messageId);
      processedMessageIds.current.add(botMessageId);

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'content' && data.text) {
                  botMessage += data.text;
                  
                  // Use the SAME Firebase ID during streaming
                  setMessages([
                    ...updatedMessages,
                    { id: botMessageId, sender: "bot", text: botMessage, isStreaming: true }
                  ]);
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      // Update the Firebase document with the complete message
      const convoRef = doc(db, 'users', userId, 'conversations', conversationId);
      const messageRef = doc(convoRef, 'messages', botMessageId);
      await updateDoc(messageRef, {
        content: botMessage
      });

      // Update UI with final message - same ID, just update isStreaming flag
      setMessages([
        userMessage,
        { id: botMessageId, sender: "bot", text: botMessage, isStreaming: false }
      ]);

      // Set the flag to prevent message disappearance
      recentlyCompletedMessageRef.current = true; // Bug Fix
      lastConversationIdRef.current = conversationId;
      setTimeout(() => {
        if (lastConversationIdRef.current === conversationId) {
          recentlyCompletedMessageRef.current = false;
        }
      }, 2000); // 2 second window provides protection against race conditions

      await incrementUsage();
      setIsFirstMessage(false);
    } catch (error) {
      console.error("Error in handleFirstMessage:", error);
      setError("Error sending message");
    } finally {
      setIsLoading(false);
      isStreamingRef.current = false;
    }
  };

  const handleNewMessage = useCallback(async (msg, mode = 'normal') => {
    setIsLoading(true);
    setError(null);
    isStreamingRef.current = true;
    
    try {
      await checkLimit();
      if (!canSendMessage) {
        setShowUpgradePrompt(true);
        setIsLoading(false);
        isStreamingRef.current = false;
        return;
      }
  
      // Get the conversation ID from activeConversation or session storage
      let currentConversationId = activeConversation?.id;
  
      // If we don't have an active conversation ID yet, check session storage
      if (!currentConversationId) {
        const savedState = sessionStorage.getItem('chatState');
        if (savedState) {
          try {
            const parsedState = JSON.parse(savedState);
            if (parsedState.conversationId) {
              currentConversationId = parsedState.conversationId;
              console.log('Retrieved conversation ID from session storage:', currentConversationId);
            } 
            else if (parsedState.activeConversation?.id) {
              currentConversationId = parsedState.activeConversation.id;
              console.log('Retrieved conversation ID from active conversation in session storage:', currentConversationId);
            }
          } catch (e) {
            console.error('Error parsing session storage for conversation ID:', e);
          }
        }
      }
      
      // Save user message to Firebase
      let userMessageId;
      
      if (!currentConversationId) {
        // Create new conversation if needed
        const result = await saveMessage(userId, { 
          sender: "user", 
          text: msg.text 
        });
        
        userMessageId = result.messageId;
        currentConversationId = result.conversationId;
  
        if (currentConversationId) {
          await selectConversation({ id: currentConversationId });
          await updatePointsOnConversation(userId);
        } else {
          throw new Error("Failed to create conversation");
        }
      } else {
        // Add to existing conversation
        const result = await saveMessage(userId, { 
          sender: "user", 
          text: msg.text 
        }, currentConversationId);
        
        userMessageId = result.messageId;
        await updatePointsOnMessage(userId);
      }
  
      // CRITICAL: Get current messages to ensure we're not losing any
      const currentMessages = [...messages];
  
      // Add user message ID to processed set
      processedMessageIds.current.add(userMessageId);
    
      // Create user message for the UI
      const userMessage = { id: userMessageId, sender: "user", text: msg.text };
      
      // Update UI with user message - APPEND to current messages
      const updatedMessages = [...currentMessages, userMessage];
      setMessages(updatedMessages);
  
      // Choose suffix based on message type or current mode
      let suffix = "";
      if (msg.isSearch) {
        suffix = RESPONSE_MODES.search;
      } else {
        suffix = RESPONSE_MODES[mode] || "";
      }
    
      const apiMessage = msg.text + suffix;
  
      // Create message history for the API
      const messageHistory = currentMessages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',  // Use 'assistant' not 'bot'
        content: msg.text
      }));
    
      const response = await fetch(`${config.apiUrl}/ai/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: apiMessage,
          history: messageHistory,
          language, 
          metadata: { userId: userId, conversationId: currentConversationId}  // As a nested metadata object
        }),
      });
  
      if (!response.ok) throw new Error('Network response was not ok');
  
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let botMessage = "";
      
      // Generate the Firebase ID upfront to maintain consistency
      const { messageId: botMessageId } = await saveMessage(userId, { 
        sender: "bot", 
        text: "" // Save placeholder first
      }, currentConversationId);
      
      // Add bot message ID to processed set
      processedMessageIds.current.add(botMessageId);
      
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'content' && data.text) {
                  botMessage += data.text;
                  
                  // Use the SAME Firebase ID during streaming
                  setMessages([
                    ...updatedMessages,
                    { id: botMessageId, sender: "bot", text: botMessage, isStreaming: true }
                  ]);
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
  
      // Update the Firebase document with the complete message
      const convoRef = doc(db, 'users', userId, 'conversations', currentConversationId);
      const messageRef = doc(convoRef, 'messages', botMessageId);
      await updateDoc(messageRef, {
        content: botMessage
      });
      
      // Final messages array - same ID, just update isStreaming flag
      const finalMessages = [
        ...updatedMessages,
        { id: botMessageId, sender: "bot", text: botMessage, isStreaming: false }
      ];
      
      // Update local state with all messages
      setMessages(finalMessages);

      // Set the flag to prevent message disappearance
      recentlyCompletedMessageRef.current = true;
      lastConversationIdRef.current = currentConversationId;
      setTimeout(() => {
        if (lastConversationIdRef.current === currentConversationId) {
          recentlyCompletedMessageRef.current = false;
        }
      }, 2000); // 2 second window is enough to prevent race conditions
      
      // Update session storage to ensure continuity
      sessionStorage.setItem('chatState', JSON.stringify({
        userId,
        conversationId: currentConversationId,
        messages: finalMessages,
        activeConversation: {
          ...activeConversation, 
          id: currentConversationId,
          lastMessage: botMessage
        }
      }));
      
      // Increment usage counter
      await incrementUsage();
    } catch (error) {
      console.error("Network Error:", error);
      setError("Network Error: Please try again later.");
    } finally {
      setIsLoading(false);
      isStreamingRef.current = false;
    }
  }, [messages, canSendMessage, checkLimit, incrementUsage, userId, language, 
      activeConversation, selectConversation, setMessages, responseMode]);

  // Filter incoming messages from Firestore to avoid duplication
  const filterDuplicateMessages = useCallback((newMessages) => {
  // If we're currently streaming, don't update from Firebase
  if (isStreamingRef.current) return messages;
  
  // Get the first message ID from both sets to check if we're comparing the same conversation
  const currentFirstId = messages.length > 0 ? messages[0].id : null;
  const newFirstId = newMessages.length > 0 ? newMessages[0].id : null;
  
  // Check if we're dealing with the same conversation by comparing first message IDs
  const isSameConversation = currentFirstId && newFirstId && currentFirstId === newFirstId;
  
  // Only block updates when:
  // 1. We're in the same conversation (first message IDs match)
  // 2. Our current message count is GREATER than the incoming Firebase message count
  // 3. We just completed a message (in the last 2 seconds)
  if (isSameConversation && 
      messages.length > newMessages.length && 
      recentlyCompletedMessageRef.current) {
    console.log('Prevented message disappearance - keeping newer local messages');
    return messages; // Keep our newer messages instead of allowing Firebase to override
  }
  
  // Very important: Update conversation ID reference when conversation changes
  if (newFirstId && newFirstId !== currentFirstId) {
    console.log('Conversation changed, updating reference');
    lastConversationIdRef.current = newFirstId;
    // Also clear the protection flag when switching conversations
   // This ensures we don't accidentally block updates in the new conversation
    recentlyCompletedMessageRef.current = false;
  }
  
  // For all other cases, allow the Firebase update
  return newMessages;
}, [messages]);

  return {
    handleFirstMessage,
    handleNewMessage,
    filterDuplicateMessages,
    isStreaming: () => isStreamingRef.current,
    resetFirstAfterTransition: () => { isFirstAfterTransitionRef.current = true; }
  };
};


// 3/1 updates: ------------------------------------------------ before the 3-6-25 history problem
// import { useState, useCallback, useRef } from 'react';
// import { saveMessage } from '../utils/db-messaging';
// import { updatePointsOnConversation } from '../userservices/updatePointsOnConversation';
// import { updatePointsOnMessage } from '../userservices/updatePointsOnMessage';
// import { config } from '../config';

// export const useMessageHandlers = ({
//   userId,
//   messages,
//   setMessages, 
//   setIsFirstMessage,
//   setIsLoading,
//   setError,
//   checkLimit,
//   canSendMessage,
//   setShowUpgradePrompt,
//   incrementUsage,
//   activeConversation,
//   selectConversation,
//   language,
//   responseMode = 'normal'
// }) => {
//   // Track whether we're currently in a streaming operation
//   const isStreamingRef = useRef(false);
//   // Track message IDs we've already processed to avoid duplication
//   const processedMessageIds = useRef(new Set());

//   // Define response mode suffixes
//   const RESPONSE_MODES = {
//     normal: "",
//     quick: "[respond briefly and directly]",
//     deep: "[provide detailed statistical analysis]",
//     search: "[. Use this to search these terms for news and provide relevant content and articles]"
//   };

//   const handleFirstMessage = async (message) => {
//     setIsLoading(true);
//     setError(null);
//     isStreamingRef.current = true;
  
//     try {
//       await checkLimit();
//       if (!canSendMessage) {
//         setShowUpgradePrompt(true);
//         setIsLoading(false);
//         isStreamingRef.current = false;
//         return;
//       }
  
//       // First save user message and create new conversation
//       const { messageId, conversationId } = await saveMessage(userId, { 
//         sender: "user", 
//         text: message.text // Save original message
//       });

//       // Make sure conversation is selected before proceeding
//       if (conversationId) {
//         await selectConversation({ id: conversationId });
//       }

//       // Update messages immediately with user message only - don't add bot message yet
//       const userMessage = { id: messageId, sender: "user", text: message.text };
//       const updatedMessages = [userMessage];
//       setMessages(updatedMessages);

//       // Choose suffix based on message type or current mode
//       let suffix = "";
//       if (message.isSearch) {
//         suffix = RESPONSE_MODES.search;
//       } else {
//         suffix = RESPONSE_MODES[responseMode] || "";
//       }

//       // Add suffix only for API call
//       const apiMessage = message.text + suffix;

//       const response = await fetch(`${config.apiUrl}/ai/ask`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           question: apiMessage, // Use modified message
//           history: [],
//           language
//         }),
//       });
  
//       if (!response.ok) {
//         throw new Error('Network response was not ok');
//       }

//       // Handle the stream
//       const reader = response.body.getReader();
//       const decoder = new TextDecoder();
//       let botMessage = "";
//       let tempMessageId = Date.now().toString(); // Temporary ID for UI updates only

//       try {
//         while (true) {
//           const { value, done } = await reader.read();
//           if (done) break;
          
//           const chunk = decoder.decode(value);
//           const lines = chunk.split('\n');
          
//           for (const line of lines) {
//             if (line.startsWith('data: ')) {
//               try {
//                 const data = JSON.parse(line.slice(6));
//                 if (data.type === 'content' && data.text) {
//                   botMessage += data.text;
                  
//                   // Update messages in real-time with the growing bot response
//                   // But use a temporary ID that won't match anything in Firebase
//                   setMessages([
//                     ...updatedMessages,
//                     { id: tempMessageId, sender: "bot", text: botMessage, isStreaming: true }
//                   ]);
//                 }
//               } catch (e) {
//                 console.error('Error parsing SSE data:', e);
//               }
//             }
//           }
//         }
//       } finally {
//         reader.releaseLock();
//       }

//       // Only save the complete bot response to Firebase ONCE when stream is done
//       const { messageId: botMessageId } = await saveMessage(userId, { 
//         sender: "bot", 
//         text: botMessage 
//       }, conversationId);

//       // Add both message IDs to processed set to avoid duplication
//       processedMessageIds.current.add(messageId);
//       processedMessageIds.current.add(botMessageId);

//       // Update UI with final message that has the real Firebase ID
//       setMessages([
//         userMessage,
//         { id: botMessageId, sender: "bot", text: botMessage, isStreaming: false }
//       ]);

//       await incrementUsage();
//       setIsFirstMessage(false);
//     } catch (error) {
//       console.error("Error in handleFirstMessage:", error);
//       setError("Error sending message");
//     } finally {
//       setIsLoading(false);
//       isStreamingRef.current = false;
//     }
//   };

//   const handleNewMessage = useCallback(async (msg, mode = 'normal') => {
//     setIsLoading(true);
//     setError(null);
//     isStreamingRef.current = true;
    
//     try {
//       await checkLimit();
//       if (!canSendMessage) {
//         setShowUpgradePrompt(true);
//         setIsLoading(false);
//         isStreamingRef.current = false;
//         return;
//       }
  
//       let currentConversationId = activeConversation?.id;
  
//       // Save user message to Firebase
//       let userMessageId;
      
//       if (!currentConversationId) {
//         // Create new conversation if needed
//         const result = await saveMessage(userId, { 
//           sender: "user", 
//           text: msg.text 
//         });
        
//         userMessageId = result.messageId;
//         currentConversationId = result.conversationId;
  
//         if (currentConversationId) {
//           await selectConversation({ id: currentConversationId });
//           await updatePointsOnConversation(userId);
//         } else {
//           throw new Error("Failed to create conversation");
//         }
//       } else {
//         // Add to existing conversation
//         const result = await saveMessage(userId, { 
//           sender: "user", 
//           text: msg.text 
//         }, currentConversationId);
        
//         userMessageId = result.messageId;
//         await updatePointsOnMessage(userId);
//       }

//       // Add user message ID to processed set
//       processedMessageIds.current.add(userMessageId);
  
//       // Create user message for the UI
//       const userMessage = { id: userMessageId, sender: "user", text: msg.text };
      
//       // Update UI with user message
//       const updatedMessages = [...messages, userMessage];
//       setMessages(updatedMessages);
  
//       // Choose suffix based on message type or current mode
//       let suffix = "";
//       if (msg.isSearch) {
//         suffix = RESPONSE_MODES.search;
//       } else {
//         suffix = RESPONSE_MODES[mode] || "";
//       }
  
//       const apiMessage = msg.text + suffix;
  
//       const response = await fetch(`${config.apiUrl}/ai/ask`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           question: apiMessage, // Modified message only for API
//           history: messages.map(msg => ({
//             role: msg.sender === 'user' ? 'user' : 'assistant',
//             content: msg.text
//           })),
//           language
//         }),
//       });

//       if (!response.ok) throw new Error('Network response was not ok');

//       const reader = response.body.getReader();
//       const decoder = new TextDecoder();
//       let botMessage = "";
//       let tempMessageId = Date.now().toString(); // Temporary ID for UI updates
      
//       try {
//         while (true) {
//           const { value, done } = await reader.read();
//           if (done) break;
          
//           const chunk = decoder.decode(value);
//           const lines = chunk.split('\n');
          
//           for (const line of lines) {
//             if (line.startsWith('data: ')) {
//               try {
//                 const data = JSON.parse(line.slice(6));
//                 if (data.type === 'content' && data.text) {
//                   botMessage += data.text;
                  
//                   // Update message with UI-only ID during streaming
//                   setMessages([
//                     ...updatedMessages,
//                     { id: tempMessageId, sender: "bot", text: botMessage, isStreaming: true }
//                   ]);
//                 }
//               } catch (e) {
//                 console.error('Error parsing SSE data:', e);
//               }
//             }
//           }
//         }
//       } finally {
//         reader.releaseLock();
//       }

//       // Only save the complete bot response to Firebase when stream is done
//       const { messageId: botMessageId } = await saveMessage(userId, { 
//         sender: "bot", 
//         text: botMessage 
//       }, currentConversationId);
      
//       // Add bot message ID to processed set
//       processedMessageIds.current.add(botMessageId);
      
//       // Update UI with final messages that have real Firebase IDs
//       setMessages([
//         ...messages,
//         userMessage,
//         { id: botMessageId, sender: "bot", text: botMessage, isStreaming: false }
//       ]);
      
//       await incrementUsage();
//     } catch (error) {
//       console.error("Network Error:", error);
//       setError("Network Error: Please try again later.");
//     } finally {
//       setIsLoading(false);
//       isStreamingRef.current = false;
//     }
//   }, [messages, canSendMessage, checkLimit, incrementUsage, userId, language, 
//       activeConversation, selectConversation, setMessages, responseMode]);

//   // Filter incoming messages from Firestore to avoid duplication
//   const filterDuplicateMessages = useCallback((newMessages) => {
//     // If we're currently streaming, don't update from Firebase
//     if (isStreamingRef.current) return messages;
    
//     // Filter out messages we've already processed
//     const filteredMessages = newMessages.filter(msg => 
//       !processedMessageIds.current.has(msg.id)
//     );
    
//     // If the filtered and current message counts match, keep current messages
//     if (filteredMessages.length === messages.length) return messages;
    
//     // Otherwise return the new filtered messages
//     return filteredMessages;
//   }, [messages]);

//   return {
//     handleFirstMessage,
//     handleNewMessage,
//     filterDuplicateMessages,
//     isStreaming: () => isStreamingRef.current
//   };
// };


// import { useState, useCallback } from 'react';
// import { saveMessage } from '../utils/db-messaging';
// import { updatePointsOnConversation } from '../userservices/updatePointsOnConversation';
// import { updatePointsOnMessage } from '../userservices/updatePointsOnMessage';
// import { config } from '../config';

// export const useMessageHandlers = ({
//   userId,
//   messages,
//   setMessages, 
//   setIsFirstMessage,
//   setIsLoading,
//   setError,
//   checkLimit,
//   canSendMessage,
//   setShowUpgradePrompt,
//   incrementUsage,
//   activeConversation,
//   selectConversation,
//   language,
//   responseMode = 'normal'
// }) => {
//   // Define response mode suffixes
//   const RESPONSE_MODES = {
//     normal: "",
//     quick: "[respond briefly and directly]",
//     deep: "[provide detailed statistical analysis]",
//     search: "[. Use this to search these terms for news and provide relevant content and articles]" // New search mode
//   };

//   const handleFirstMessage = async (message) => {
//     setIsLoading(true);
//     setError(null);
  
//     try {
//       await checkLimit();
//       if (!canSendMessage) {
//         setShowUpgradePrompt(true);
//         setIsLoading(false);
//         return;
//       }
  
//       // First save user message and create new conversation
//       const { messageId, conversationId } = await saveMessage(userId, { 
//         sender: "user", 
//         text: message.text // Save original message
//       });

//       // Make sure conversation is selected before proceeding
//       if (conversationId) {
//         await selectConversation({ id: conversationId });
//       }

//       // Update messages immediately with user message
//       const updatedMessages = [{ sender: "user", text: message.text }];
//       setMessages(updatedMessages);

//       // Choose suffix based on message type or current mode
//       let suffix = "";
//       if (message.isSearch) {
//         suffix = RESPONSE_MODES.search;
//       } else {
//         suffix = RESPONSE_MODES[responseMode] || "";
//       }

//       // Add suffix only for API call
//       const apiMessage = message.text + suffix;

//       const response = await fetch(`${config.apiUrl}/ai/ask`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           question: apiMessage, // Use modified message
//           history: [],
//           language
//         }),
//       });
  
//       if (!response.ok) {
//         throw new Error('Network response was not ok');
//       }

//       // Handle the stream
//       const reader = response.body.getReader();
//       const decoder = new TextDecoder();
//       let botMessage = "";

//       try {
//         while (true) {
//           const { value, done } = await reader.read();
//           if (done) break;
          
//           const chunk = decoder.decode(value);
//           const lines = chunk.split('\n');
          
//           for (const line of lines) {
//             if (line.startsWith('data: ')) {
//               try {
//                 const data = JSON.parse(line.slice(6));
//                 if (data.type === 'content' && data.text) {
//                   botMessage += data.text;
//                   // Update messages in real-time with the growing bot response
//                   setMessages([
//                     ...updatedMessages,
//                     { sender: "bot", text: botMessage }
//                   ]);
//                 }
//               } catch (e) {
//                 console.error('Error parsing SSE data:', e);
//               }
//             }
//           }
//         }
//       } finally {
//         reader.releaseLock();
//       }

//       // Save the complete bot response
//       await saveMessage(userId, { 
//         sender: "bot", 
//         text: botMessage 
//       }, conversationId);

//       await incrementUsage();
//       setIsFirstMessage(false);
//     } catch (error) {
//       console.error("Error in handleFirstMessage:", error);
//       setError("Error sending message");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleNewMessage = useCallback(async (msg, mode = 'normal') => {
//     console.log("🔄 Message handler called with:", msg);
//     setIsLoading(true);
//     setError(null);
    
//     try {
//       await checkLimit();
//       if (!canSendMessage) {
//         setShowUpgradePrompt(true);
//         setIsLoading(false);
//         return;
//       }
  
//       let currentConversationId = activeConversation?.id;
  
//       // Save the original message to display
//       const messageToDisplay = { sender: "user", text: msg.text };
  
//       if (!currentConversationId) {
//         const { messageId, conversationId } = await saveMessage(userId, messageToDisplay);
  
//         if (conversationId) {
//           await selectConversation({ id: conversationId });
//           await updatePointsOnConversation(userId);
//           currentConversationId = conversationId;
//         } else {
//           throw new Error("Failed to create conversation");
//         }
//       }
  
//       await saveMessage(userId, messageToDisplay, currentConversationId);
//       await updatePointsOnMessage(userId);
  
//       // Update UI with original message
//       const updatedMessages = [...messages, messageToDisplay];
//       setMessages(updatedMessages);
  
//       // Choose suffix based on message type or current mode
//       let suffix = "";
//       if (msg.isSearch) {
//         suffix = RESPONSE_MODES.search;
//       } else {
//         suffix = RESPONSE_MODES[mode] || "";
//       }
  
//       const apiMessage = msg.text + suffix;
  
//       const response = await fetch(`${config.apiUrl}/ai/ask`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           question: apiMessage, // Modified message only for API
//           history: messages.map(msg => ({
//             role: msg.sender === 'user' ? 'user' : 'assistant',
//             content: msg.text
//           })),
//           language
//         }),
//       });

//       if (!response.ok) throw new Error('Network response was not ok');

//       const reader = response.body.getReader();
//       const decoder = new TextDecoder();
//       let botMessage = "";

//       try {
//         while (true) {
//           const { value, done } = await reader.read();
//           if (done) break;
          
//           const chunk = decoder.decode(value);
//           const lines = chunk.split('\n');
          
//           for (const line of lines) {
//             if (line.startsWith('data: ')) {
//               try {
//                 const data = JSON.parse(line.slice(6));
//                 if (data.type === 'content' && data.text) {
//                   botMessage += data.text;
//                   setMessages([
//                     ...updatedMessages,
//                     { sender: "bot", text: botMessage }
//                   ]);
//                 }
//               } catch (e) {
//                 console.error('Error parsing SSE data:', e);
//               }
//             }
//           }
//         }
//       } finally {
//         reader.releaseLock();
//       }

//       await saveMessage(userId, { 
//         sender: "bot", 
//         text: botMessage 
//       }, currentConversationId);
      
//       await incrementUsage();

//     } catch (error) {
//       console.error("Network Error:", error);
//       setError("Network Error: Please try again later.");
//     } finally {
//       setIsLoading(false);
//     }
//   }, [messages, canSendMessage, checkLimit, incrementUsage, userId, language, 
//       activeConversation, selectConversation, setMessages, responseMode]); 

//   return {
//     handleFirstMessage,
//     handleNewMessage
//   };
// };

// import { useState, useCallback } from 'react';
// import { saveMessage } from '../utils/db-messaging';
// import { updatePointsOnConversation } from '../userservices/updatePointsOnConversation';
// import { updatePointsOnMessage } from '../userservices/updatePointsOnMessage';
// import { config } from '../config';

// export const useMessageHandlers = ({
//   userId,
//   messages,
//   setMessages, 
//   setIsFirstMessage,
//   setIsLoading,
//   setError,
//   checkLimit,
//   canSendMessage,
//   setShowUpgradePrompt,
//   incrementUsage,
//   activeConversation,
//   selectConversation,
//   language,
//   responseMode = 'normal' // New parameter with default
// }) => {
//   // Define response mode suffixes
//   const RESPONSE_MODES = {
//     normal: "",
//     quick: "[respond briefly and directly]",
//     deep: "[provide detailed statistical analysis]"
//   };

//   const handleFirstMessage = async (message) => {
//     setIsLoading(true);
//     setError(null);
  
//     try {
//       await checkLimit();
//       if (!canSendMessage) {
//         setShowUpgradePrompt(true);
//         setIsLoading(false);
//         return;
//       }
  
//       // First save user message and create new conversation
//       const { messageId, conversationId } = await saveMessage(userId, { 
//         sender: "user", 
//         text: message.text // Save original message
//       });

//       // Make sure conversation is selected before proceeding
//       if (conversationId) {
//         await selectConversation({ id: conversationId });
//       }

//       // Update messages immediately with user message
//       const updatedMessages = [{ sender: "user", text: message.text }];
//       setMessages(updatedMessages);

//       // Add suffix only for API call
//       const apiMessage = message.text + (RESPONSE_MODES[responseMode] || "");

//       const response = await fetch(`${config.apiUrl}/ai/ask`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           question: apiMessage, // Use modified message
//           history: [],
//           language
//         }),
//       });
  
//       if (!response.ok) {
//         throw new Error('Network response was not ok');
//       }

//       // Handle the stream
//       const reader = response.body.getReader();
//       const decoder = new TextDecoder();
//       let botMessage = "";

//       try {
//         while (true) {
//           const { value, done } = await reader.read();
//           if (done) break;
          
//           const chunk = decoder.decode(value);
//           const lines = chunk.split('\n');
          
//           for (const line of lines) {
//             if (line.startsWith('data: ')) {
//               try {
//                 const data = JSON.parse(line.slice(6));
//                 if (data.type === 'content' && data.text) {
//                   botMessage += data.text;
//                   // Update messages in real-time with the growing bot response
//                   setMessages([
//                     ...updatedMessages,
//                     { sender: "bot", text: botMessage }
//                   ]);
//                 }
//               } catch (e) {
//                 console.error('Error parsing SSE data:', e);
//               }
//             }
//           }
//         }
//       } finally {
//         reader.releaseLock();
//       }

//       // Save the complete bot response
//       await saveMessage(userId, { 
//         sender: "bot", 
//         text: botMessage 
//       }, conversationId);

//       await incrementUsage();
//       setIsFirstMessage(false);
//     } catch (error) {
//       console.error("Error in handleFirstMessage:", error);
//       setError("Error sending message");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleNewMessage = useCallback(async (msg, mode = 'normal') => {
//     console.log("🔄 Message handler called with:", msg);
//     setIsLoading(true);
//     setError(null);
    
//     try {
//       await checkLimit();
//       if (!canSendMessage) {
//         setShowUpgradePrompt(true);
//         setIsLoading(false);
//         return;
//       }
  
//       let currentConversationId = activeConversation?.id;
  
//       // Save the original message to display
//       const messageToDisplay = { sender: "user", text: msg.text };
  
//       if (!currentConversationId) {
//         const { messageId, conversationId } = await saveMessage(userId, messageToDisplay);
  
//         if (conversationId) {
//           await selectConversation({ id: conversationId });
//           await updatePointsOnConversation(userId);
//           currentConversationId = conversationId;
//         } else {
//           throw new Error("Failed to create conversation");
//         }
//       }
  
//       await saveMessage(userId, messageToDisplay, currentConversationId);
//       await updatePointsOnMessage(userId);
  
//       // Update UI with original message
//       const updatedMessages = [...messages, messageToDisplay];
//       setMessages(updatedMessages);
  
//       // Add suffix only for API call
//       const RESPONSE_MODES = {
//         normal: "",
//         quick: "[respond briefly and directly]",
//         deep: "[provide detailed statistical analysis]"
//       };
  
//       const apiMessage = msg.text + (RESPONSE_MODES[mode] || "");
  
//       const response = await fetch(`${config.apiUrl}/ai/ask`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           question: apiMessage, // Modified message only for API
//           history: messages.map(msg => ({
//             role: msg.sender === 'user' ? 'user' : 'assistant',
//             content: msg.text
//           })),
//           language
//         }),
//       });

//       if (!response.ok) throw new Error('Network response was not ok');

//       const reader = response.body.getReader();
//       const decoder = new TextDecoder();
//       let botMessage = "";

//       try {
//         while (true) {
//           const { value, done } = await reader.read();
//           if (done) break;
          
//           const chunk = decoder.decode(value);
//           const lines = chunk.split('\n');
          
//           for (const line of lines) {
//             if (line.startsWith('data: ')) {
//               try {
//                 const data = JSON.parse(line.slice(6));
//                 if (data.type === 'content' && data.text) {
//                   botMessage += data.text;
//                   setMessages([
//                     ...updatedMessages,
//                     { sender: "bot", text: botMessage }
//                   ]);
//                 }
//               } catch (e) {
//                 console.error('Error parsing SSE data:', e);
//               }
//             }
//           }
//         }
//       } finally {
//         reader.releaseLock();
//       }

//       await saveMessage(userId, { 
//         sender: "bot", 
//         text: botMessage 
//       }, currentConversationId);
      
//       await incrementUsage();

//     } catch (error) {
//       console.error("Network Error:", error);
//       setError("Network Error: Please try again later.");
//     } finally {
//       setIsLoading(false);
//     }
//   }, [messages, canSendMessage, checkLimit, incrementUsage, userId, language, 
//       activeConversation, selectConversation, setMessages, responseMode]); // Added responseMode to deps

//   return {
//     handleFirstMessage,
//     handleNewMessage
//   };
// };

// working 2/20/25
// import { useState, useCallback } from 'react';
// import { saveMessage } from '../utils/db-messaging';
// import { updatePointsOnConversation } from '../userservices/updatePointsOnConversation';
// import { updatePointsOnMessage } from '../userservices/updatePointsOnMessage';
// import { config } from '../config';

// export const useMessageHandlers = ({
//   userId,
//   messages,
//   setMessages, 
//   setIsFirstMessage,
//   setIsLoading,
//   setError,
//   checkLimit,
//   canSendMessage,
//   setShowUpgradePrompt,
//   incrementUsage,
//   activeConversation,
//   selectConversation,
//   language
// }) => {
//   const handleFirstMessage = async (message) => {
//     setIsLoading(true);
//     setError(null);
  
//     try {
//       await checkLimit();
//       if (!canSendMessage) {
//         setShowUpgradePrompt(true);
//         setIsLoading(false);
//         return;
//       }
  
//       // First save user message and create new conversation
//       const { messageId, conversationId } = await saveMessage(userId, { 
//         sender: "user", 
//         text: message.text 
//       });

//       // Make sure conversation is selected before proceeding
//       if (conversationId) {
//         await selectConversation({ id: conversationId });
//       }

//       // Update messages immediately with user message
//       const updatedMessages = [{ sender: "user", text: message.text }];
//       setMessages(updatedMessages);

//       const response = await fetch(`${config.apiUrl}/ai/ask`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           question: message.text,
//           history: [],
//           language
//         }),
//       });
  
//       if (!response.ok) {
//         throw new Error('Network response was not ok');
//       }

//       // Handle the stream
//       const reader = response.body.getReader();
//       const decoder = new TextDecoder();
//       let botMessage = "";

//       try {
//         while (true) {
//           const { value, done } = await reader.read();
//           if (done) break;
          
//           const chunk = decoder.decode(value);
//           const lines = chunk.split('\n');
          
//           for (const line of lines) {
//             if (line.startsWith('data: ')) {
//               try {
//                 const data = JSON.parse(line.slice(6));
//                 if (data.type === 'content' && data.text) {
//                   botMessage += data.text;
//                   // Update messages in real-time with the growing bot response
//                   setMessages([
//                     ...updatedMessages,
//                     { sender: "bot", text: botMessage }
//                   ]);
//                 }
//               } catch (e) {
//                 console.error('Error parsing SSE data:', e);
//               }
//             }
//           }
//         }
//       } finally {
//         reader.releaseLock();
//       }

//       // Save the complete bot response
//       await saveMessage(userId, { 
//         sender: "bot", 
//         text: botMessage 
//       }, conversationId);

//       await incrementUsage();
//       setIsFirstMessage(false);
//     } catch (error) {
//       console.error("Error in handleFirstMessage:", error);
//       setError("Error sending message");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleNewMessage = useCallback(async (msg) => {
//     console.log("🔄 Message handler called with:", msg);
//     setIsLoading(true);
//     setError(null);
    
//     try {
//       await checkLimit();
//       if (!canSendMessage) {
//         setShowUpgradePrompt(true);
//         setIsLoading(false);
//         return;
//       }

//       let currentConversationId = activeConversation?.id;

//       if (!currentConversationId) {
//         const { messageId, conversationId } = await saveMessage(userId, { 
//           sender: "user", 
//           text: msg.text 
//         });

//         if (conversationId) {
//           await selectConversation({ id: conversationId });
//           await updatePointsOnConversation(userId);
//           currentConversationId = conversationId;
//         } else {
//           throw new Error("Failed to create conversation");
//         }
//       }

//       await saveMessage(userId, { 
//         sender: "user", 
//         text: msg.text 
//       }, currentConversationId);

//       await updatePointsOnMessage(userId);

//       const updatedMessages = [...messages, { sender: "user", text: msg.text }];
//       setMessages(updatedMessages);

//       const response = await fetch(`${config.apiUrl}/ai/ask`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           question: msg.text,
//           history: messages.map(msg => ({
//             role: msg.sender === 'user' ? 'user' : 'assistant',
//             content: msg.text
//           })),
//           language
//         }),
//       });

//       if (!response.ok) throw new Error('Network response was not ok');

//       const reader = response.body.getReader();
//       const decoder = new TextDecoder();
//       let botMessage = "";

//       try {
//         while (true) {
//           const { value, done } = await reader.read();
//           if (done) break;
          
//           const chunk = decoder.decode(value);
//           const lines = chunk.split('\n');
          
//           for (const line of lines) {
//             if (line.startsWith('data: ')) {
//               try {
//                 const data = JSON.parse(line.slice(6));
//                 if (data.type === 'content' && data.text) {
//                   botMessage += data.text;
//                   setMessages([
//                     ...updatedMessages,
//                     { sender: "bot", text: botMessage }
//                   ]);
//                 }
//               } catch (e) {
//                 console.error('Error parsing SSE data:', e);
//               }
//             }
//           }
//         }
//       } finally {
//         reader.releaseLock();
//       }

//       await saveMessage(userId, { 
//         sender: "bot", 
//         text: botMessage 
//       }, currentConversationId);
      
//       await incrementUsage();

//     } catch (error) {
//       console.error("Network Error:", error);
//       setError("Network Error: Please try again later.");
//     } finally {
//       setIsLoading(false);
//     }
//   }, [messages, canSendMessage, checkLimit, incrementUsage, userId, language, 
//       activeConversation, selectConversation, setMessages]);

//   return {
//     handleFirstMessage,
//     handleNewMessage
//   };
// };


// import { useState, useCallback } from 'react';
// import { saveMessage } from '../utils/db-messaging';
// import { updatePointsOnConversation } from '../userservices/updatePointsOnConversation';
// import { updatePointsOnMessage } from '../userservices/updatePointsOnMessage';
// import { config } from '../config';

// export const useMessageHandlers = ({
//   userId,
//   messages,
//   setMessages, 
//   setIsFirstMessage,
//   setIsLoading,
//   setError,
//   checkLimit,
//   canSendMessage,
//   setShowUpgradePrompt,
//   incrementUsage,
//   activeConversation,
//   selectConversation,
//   language
// }) => {
//   const handleFirstMessage = async (message) => {
//     setIsLoading(true);
//     setError(null);
  
//     try {
//       await checkLimit();
//       if (!canSendMessage) {
//         setShowUpgradePrompt(true);
//         setIsLoading(false);
//         return;
//       }
  
//       const { messageId, conversationId } = await saveMessage(userId, { 
//         sender: "user", 
//         text: message.text 
//       });

//       if (conversationId) {
//         await selectConversation({ id: conversationId });
//       }

//       const response = await fetch(`${config.apiUrl}/ai/ask`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           question: message.text,
//           history: [],
//           language
//         }),
//       });
  
//       const data = await response.json();
  
//       if (response.ok) {
//         await saveMessage(userId, { 
//           sender: "bot", 
//           text: data.response 
//         }, conversationId);

//         await incrementUsage();
//         setIsFirstMessage(false);
//       } else {
//         throw new Error(data.error || "Error processing message");
//       }
//     } catch (error) {
//       console.error("Error in handleFirstMessage:", error);
//       setError("Error sending message");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleNewMessage = useCallback(async (msg) => {
//     console.log("🔄 Message handler called with:", msg);
//     setIsLoading(true);
//     setError(null);
    
//     try {
//       await checkLimit();
//       if (!canSendMessage) {
//         setShowUpgradePrompt(true);
//         setIsLoading(false);
//         return;
//       }

//       let currentConversationId = activeConversation?.id;

//       if (!currentConversationId) {
//         const { messageId, conversationId } = await saveMessage(userId, { 
//           sender: "user", 
//           text: msg.text 
//         });

//         if (conversationId) {
//           await selectConversation({ id: conversationId });
//           await updatePointsOnConversation(userId);
//           currentConversationId = conversationId;
//         } else {
//           throw new Error("Failed to create conversation");
//         }
//       }

//       await saveMessage(userId, { 
//         sender: "user", 
//         text: msg.text 
//       }, currentConversationId);

//       await updatePointsOnMessage(userId);

//       const updatedMessages = [...messages, { sender: "user", text: msg.text }];
//       setMessages(updatedMessages);

//       const response = await fetch(`${config.apiUrl}/ai/ask`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           question: msg.text,
//           history: messages.map(msg => ({
//             role: msg.sender === 'user' ? 'user' : 'assistant',
//             content: msg.text
//           })),
//           language
//         }),
//       });

//       if (!response.ok) throw new Error('Network response was not ok');

//       const reader = response.body.getReader();
//       const decoder = new TextDecoder();
//       let botMessage = "";

//       try {
//         while (true) {
//           const { value, done } = await reader.read();
//           if (done) break;
          
//           const chunk = decoder.decode(value);
//           const lines = chunk.split('\n');
          
//           for (const line of lines) {
//             if (line.startsWith('data: ')) {
//               try {
//                 const data = JSON.parse(line.slice(6));
//                 if (data.type === 'content' && data.text) {
//                   botMessage += data.text;
//                   setMessages([
//                     ...updatedMessages,
//                     { sender: "bot", text: botMessage }
//                   ]);
//                 }
//               } catch (e) {
//                 console.error('Error parsing SSE data:', e);
//               }
//             }
//           }
//         }
//       } finally {
//         reader.releaseLock();
//       }

//       await saveMessage(userId, { 
//         sender: "bot", 
//         text: botMessage 
//       }, currentConversationId);
      
//       await incrementUsage();

//     } catch (error) {
//       console.error("Network Error:", error);
//       setError("Network Error: Please try again later.");
//     } finally {
//       setIsLoading(false);
//     }
//   }, [messages, canSendMessage, checkLimit, incrementUsage, userId, language, 
//       activeConversation, selectConversation, setMessages]);

//   return {
//     handleFirstMessage,
//     handleNewMessage
//   };
// };